import { useState, useEffect } from 'react';
import { updateDoc, doc } from 'firebase/firestore';
import { Firestore } from 'firebase/firestore';
import { Unit } from '../../types';
import { Edit2, Save, X } from 'lucide-react';

interface GeneralTabProps {
  unit: Unit;
  setUnit: (unit: Unit) => void;
  canEdit: boolean;
  db: Firestore;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  refreshUnits: () => Promise<void>;
  formatDate: (date: Date) => string;
  Button: any;
  Input: any;
  Label: any;
  Textarea: any;
  Card: any;
  CardContent: any;
  CardDescription: any;
  CardHeader: any;
  CardTitle: any;
}

export function GeneralTab({
  unit,
  setUnit,
  canEdit,
  db,
  showSuccess,
  showError,
  refreshUnits,
  formatDate,
  Button,
  Input,
  Label,
  Textarea,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
}: GeneralTabProps) {
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedName, setEditedName] = useState(unit.name);
  const [editedNumber, setEditedNumber] = useState(unit.unitNumber);
  const [editedDescription, setEditedDescription] = useState(unit.description || '');
  const [editedContactEmail, setEditedContactEmail] = useState(unit.contactEmail || '');
  const [editedContactPhone, setEditedContactPhone] = useState(unit.contactPhone || '');
  const [editedAddress, setEditedAddress] = useState(unit.address || '');

  // Sync state when unit prop changes
  useEffect(() => {
    if (!isEditingGeneral) {
      setEditedName(unit.name);
      setEditedNumber(unit.unitNumber);
      setEditedDescription(unit.description || '');
      setEditedContactEmail(unit.contactEmail || '');
      setEditedContactPhone(unit.contactPhone || '');
      setEditedAddress(unit.address || '');
    }
  }, [unit, isEditingGeneral]);

  const handleSaveGeneral = async () => {
    if (!editedName.trim()) {
      showError('Unit name is required');
      return;
    }

    if (!editedNumber.trim()) {
      showError('Unit number is required');
      return;
    }

    setSaving(true);
    try {
      const updatedSettings = {
        ...unit.settings,
      };

      await updateDoc(doc(db, 'units', unit.id), {
        name: editedName,
        unitNumber: editedNumber,
        description: editedDescription,
        contactEmail: editedContactEmail,
        contactPhone: editedContactPhone,
        address: editedAddress,
        settings: updatedSettings,
      });

      setUnit({
        ...unit,
        name: editedName,
        unitNumber: editedNumber,
        description: editedDescription,
        contactEmail: editedContactEmail,
        contactPhone: editedContactPhone,
        address: editedAddress,
        settings: updatedSettings,
      });

      setIsEditingGeneral(false);
      showSuccess('Unit information updated successfully!');
      await refreshUnits();
    } catch (error) {
      console.error('Error updating unit:', error);
      showError('Failed to update unit information');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Unit Information</CardTitle>
            <CardDescription>Basic details about your unit</CardDescription>
          </div>
          {canEdit && !isEditingGeneral && (
            <Button variant="outline" onClick={() => setIsEditingGeneral(true)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditingGeneral ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="unitName">Unit Name *</Label>
                <Input
                  id="unitName"
                  value={editedName}
                  onChange={(e: any) => setEditedName(e.target.value)}
                  placeholder="Veritas Unit"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="unitNumber">Unit Number *</Label>
                <Input
                  id="unitNumber"
                  value={editedNumber}
                  onChange={(e: any) => setEditedNumber(e.target.value)}
                  placeholder="No. 1234"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editedDescription}
                  onChange={(e: any) => setEditedDescription(e.target.value)}
                  placeholder="Tell us about your unit..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={editedContactEmail}
                  onChange={(e: any) => setEditedContactEmail(e.target.value)}
                  placeholder="secretary@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  type="tel"
                  value={editedContactPhone}
                  onChange={(e: any) => setEditedContactPhone(e.target.value)}
                  placeholder="020 1234 5678"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={editedAddress}
                  onChange={(e: any) => setEditedAddress(e.target.value)}
                  placeholder="Freemasons' Hall, 60 Great Queen Street, London, WC2B 5AZ"
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveGeneral} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditingGeneral(false);
                    setEditedName(unit.name);
                    setEditedNumber(unit.unitNumber);
                    setEditedDescription(unit.description || '');
                    setEditedContactEmail(unit.contactEmail || '');
                    setEditedContactPhone(unit.contactPhone || '');
                    setEditedAddress(unit.address || '');
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>Unit Name</Label>
                <div className="text-lg font-semibold">{unit.name}</div>
              </div>
              <div>
                <Label>Unit Number</Label>
                <div className="text-lg font-semibold">{unit.unitNumber}</div>
              </div>
              {unit.description && (
                <div>
                  <Label>Description</Label>
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {unit.description}
                  </div>
                </div>
              )}
              {unit.contactEmail && (
                <div>
                  <Label>Contact Email</Label>
                  <div className="text-sm">{unit.contactEmail}</div>
                </div>
              )}
              {unit.contactPhone && (
                <div>
                  <Label>Contact Phone</Label>
                  <div className="text-sm">{unit.contactPhone}</div>
                </div>
              )}
              {unit.address && (
                <div>
                  <Label>Address</Label>
                  <div className="text-sm whitespace-pre-wrap">{unit.address}</div>
                </div>
              )}
              <div>
                <Label>Created</Label>
                <div>{formatDate(unit.createdAt)}</div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

